import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getCameraNotSendingUrl, getCameraApiCode } from "../../config/adminApi";
import { Card } from "../Card";
import { formatTimestamp } from "./utils";

const CameraNotSending = () => {
  const { t } = useTranslation();
  const [cameraData, setCameraData] = useState({
    camerasNotSending: [],
    count: 0,
    timestamp: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCameraData = async () => {
      try {
        setLoading(true);
        const apiCode = getCameraApiCode();
        const response = await fetch(getCameraNotSendingUrl(), {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "x-functions-key": apiCode,
          },
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch camera data: ${response.status} ${response.statusText}`
          );
        }

        const data = await response.json();
        setCameraData({
          camerasNotSending: data.camerasNotSending || [],
          count: data.count || 0,
          timestamp: data.timestamp,
        });
        setLoading(false);
      } catch (err) {
        console.error("Error fetching camera data:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchCameraData();
  }, []);

  if (loading) {
    return (
      <Card className="p-6 rounded-xl shadow-sm border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t("admin_camera_not_sending")}
        </h2>
        <p className="text-gray-500 text-sm">{t("admin_loading_camera")}</p>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6 rounded-xl shadow-sm border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t("admin_camera_not_sending")}
        </h2>
        <p className="text-red-600 text-sm">{t("admin_error")}: {error}</p>
      </Card>
    );
  }

  return (
    <Card className="p-6 rounded-xl shadow-sm border border-gray-200">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {t("admin_camera_not_sending")}
          </h2>
          {cameraData.timestamp && (
            <p className="text-sm text-gray-500 mt-1">
              {t("admin_last_updated")}: {formatTimestamp(cameraData.timestamp)}
            </p>
          )}
        </div>
        <span className="px-3 py-1.5 bg-amber-50 text-amber-800 rounded-lg text-sm font-medium border border-amber-100">
          {cameraData.count} {cameraData.count === 1 ? t("admin_camera_singular") : t("admin_cameras_plural")} {t("admin_not_sending")}
        </span>
      </div>

      {cameraData.camerasNotSending.length === 0 ? (
        <p className="text-gray-500 text-sm">{t("admin_all_cameras_ok")}</p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t("admin_building_name")}
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t("admin_camera_id")}
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t("admin_last_report_time")}
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t("admin_time_since_last_report")}
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t("admin_configured_delay")}
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t("admin_threshold")}
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t("admin_building_type")}
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t("admin_status")}
                </th>
              </tr>
            </thead>
            <tbody>
              {cameraData.camerasNotSending.map((camera) => (
                <tr
                  key={camera.cameraGuid}
                  className="border-b border-gray-100 hover:bg-gray-50/80 transition-colors"
                >
                  <td className="px-5 py-3 font-medium text-gray-900">
                    {camera.buildingName || t("unknown")}
                  </td>
                  <td className="px-5 py-3 font-mono text-sm text-gray-700">
                    {camera.cameraGuid}
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-600">
                    {formatTimestamp(camera.lastReportTime)}
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-700">
                    <div className="flex flex-col">
                      <span>
                        {Math.floor(camera.timeSinceLastReport / 60)} min
                      </span>
                      <span className="text-xs text-gray-500">
                        ({Math.floor(camera.timeSinceLastReport / 3600)} h)
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-700 tabular-nums">
                    {camera.configuredDelay} s
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-700 tabular-nums">
                    {camera.thresholdMinutes} min
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-700">
                    {camera.buildingType === "0"
                      ? t("admin_block_house")
                      : camera.buildingType === "1"
                      ? t("admin_other_building")
                      : t("unknown")}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`inline-flex px-2.5 py-1 rounded-lg text-xs font-medium ${
                        camera.timeSinceLastReport >
                        camera.thresholdMinutes * 60
                          ? "bg-red-50 text-red-700 border border-red-100"
                          : "bg-amber-50 text-amber-800 border border-amber-100"
                      }`}
                    >
                      {camera.timeSinceLastReport > camera.thresholdMinutes * 60
                        ? t("admin_critical")
                        : t("admin_warning")}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default CameraNotSending;
